import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Search, AlertCircle, Wrench } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { Equipment } from '@/types/settings';
import { dataService } from '@/services/data-service';
import { formatCurrency } from '@/lib/format-currency';

// Helper component to render equipment table body content - extracted to fix nested ternary (S3358)
function EquipmentTableBody({
  isLoading,
  equipment,
  searchTerm,
  selectedEquipmentIds,
  onSelectEquipment,
}: {
  isLoading: boolean;
  equipment: Equipment[];
  searchTerm: string;
  selectedEquipmentIds: Set<string>;
  onSelectEquipment: (equipmentId: string, checked: boolean) => void;
}) {
  if (isLoading) {
    return (
      <TableRow>
        <TableCell colSpan={5} className="text-center py-12">
          <div className="flex items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            <span className="text-muted-foreground">Loading equipment...</span>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  if (equipment.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={5} className="text-center py-12">
          <div className="flex flex-col items-center gap-2">
            <Wrench className="h-8 w-8 text-muted-foreground/50" />
            <p className="text-muted-foreground">
              {searchTerm ? 'No equipment found matching your search' : 'No equipment available'}
            </p>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {equipment.map((eq) => (
        <TableRow
          key={eq.id}
          className="cursor-pointer hover:bg-muted/50"
          onClick={() => onSelectEquipment(eq.id, !selectedEquipmentIds.has(eq.id))}
        >
          <TableCell onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selectedEquipmentIds.has(eq.id)}
              onCheckedChange={(checked) => onSelectEquipment(eq.id, checked as boolean)}
            />
          </TableCell>
          <TableCell className="font-medium">
            <div className="flex items-center gap-2">
              <Wrench className="h-4 w-4 text-muted-foreground" />
              {eq.code}
            </div>
          </TableCell>
          <TableCell>{eq.description ?? '-'}</TableCell>
          <TableCell>
            {eq.acquiredDate ? new Date(eq.acquiredDate).toLocaleDateString() : '-'}
          </TableCell>
          <TableCell className="text-right">
            {formatCurrency(eq.acquisitionValue)}
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

interface EquipmentSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (selectedEquipment: Equipment[]) => void;
  excludeEquipmentIds?: string[]; // Equipment IDs to exclude from selection (already linked)
  initialSelectedEquipmentIds?: string[]; // Equipment IDs that are already selected
}

export function EquipmentSelectionDialog({ 
  open, 
  onOpenChange, 
  onConfirm,
  excludeEquipmentIds = [],
  initialSelectedEquipmentIds = [],
}: Readonly<EquipmentSelectionDialogProps>) {
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<Set<string>>(new Set(initialSelectedEquipmentIds));
  const [searchTerm, setSearchTerm] = useState('');
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load equipment when dialog opens
  useEffect(() => {
    if (open) {
      loadEquipment();
      // Reset selected equipment to initial values when dialog opens
      setSelectedEquipmentIds(new Set(initialSelectedEquipmentIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadEquipment = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const equipmentData = await dataService.getEquipment();
      setEquipment(equipmentData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load equipment');
      setEquipment([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter equipment based on search and exclusions
  const filteredEquipment = equipment.filter((eq) => {
    // Exclude already linked equipment
    if (excludeEquipmentIds.includes(eq.id)) return false;
    
    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        eq.code.toLowerCase().includes(search) ||
        eq.description?.toLowerCase().includes(search)
      );
    }
    
    return true;
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedEquipmentIds(new Set(filteredEquipment.map((eq) => eq.id)));
    } else {
      setSelectedEquipmentIds(new Set());
    }
  };

  const handleSelectEquipment = (equipmentId: string, checked: boolean) => {
    const newSet = new Set(selectedEquipmentIds);
    if (checked) {
      newSet.add(equipmentId);
    } else {
      newSet.delete(equipmentId);
    }
    setSelectedEquipmentIds(newSet);
  };

  const handleConfirm = () => {
    const selected = equipment.filter((eq) => selectedEquipmentIds.has(eq.id));
    onConfirm(selected);
    // Reset state
    setSelectedEquipmentIds(new Set());
    setSearchTerm('');
  };

  const allSelected = filteredEquipment.length > 0 && filteredEquipment.every((eq) => selectedEquipmentIds.has(eq.id));
  const canProceed = selectedEquipmentIds.size > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Select Equipment to Link</DialogTitle>
          <DialogDescription>
            Choose one or more equipment items to link to this asset
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Search */}
          <div className="space-y-2">
            <Label htmlFor="search">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                placeholder="Search by code or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          {/* Selection Info */}
          <div className="flex items-center justify-between py-2 border-y">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="select-all"
                  checked={allSelected}
                  onCheckedChange={handleSelectAll}
                />
                <Label htmlFor="select-all" className="cursor-pointer">
                  Select all ({filteredEquipment.length})
                </Label>
              </div>
              <Badge variant="secondary">
                {selectedEquipmentIds.size} selected
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground">
              Showing {filteredEquipment.length} equipment items
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Equipment Table */}
          <div className="flex-1 overflow-hidden border rounded-lg">
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader className="sticky top-0 bg-muted z-10">
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Acquired Date</TableHead>
                    <TableHead className="text-right">Acquisition Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="bg-white dark:bg-background">
                  <EquipmentTableBody
                    isLoading={isLoading}
                    equipment={filteredEquipment}
                    searchTerm={searchTerm}
                    selectedEquipmentIds={selectedEquipmentIds}
                    onSelectEquipment={handleSelectEquipment}
                  />
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canProceed}>
            Link Equipment ({selectedEquipmentIds.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
